import { Link } from 'react-router-dom';
import Logo from '@/components/feature/Logo';

const missionPillars = [
  {
    icon: 'ri-lock-unlock-line',
    title: 'Financial Liberation',
    text: 'At the core of our operational philosophy lies an unwavering commitment to dismantling the structural limitations imposed by physical currency. We recognize that cash, while historically fundamental, represents inefficiency, security vulnerability, and exclusion in the modern digital economy. Our mission is to architect and deliver a comprehensive digital financial platform that transcends these constraints — one that is impenetrably secure, intuitively accessible, and genuinely empowering for every user, from the unbanked individual in a remote community to the sophisticated enterprise managing millions in daily transactions.',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Instantaneous Empowerment',
    text: 'Speed is not merely a convenience in modern finance — it is an economic imperative. We are dedicated to ensuring that every financial action, whether sending remittances to family members, executing business payments, or managing personal savings, occurs in real time. Through proprietary transaction processing architecture and strategic partnerships with central banking networks, we have eliminated the latency that has traditionally plagued financial transfers. When you press send, your money moves — instantly, transparently, and without compromise.',
  },
  {
    icon: 'ri-exchange-dollar-line',
    title: 'Superior Infrastructure',
    text: 'The foundation of trust in digital finance rests upon three pillars: velocity, security, and affordability. Our infrastructure is engineered to excel across all three dimensions. Every transaction traverses bank-grade 256-bit encryption protocols, fortified by multi-factor authentication and biometric verification systems. Simultaneously, we have reengineered the cost structure of digital payments, eliminating the exorbitant fees that have historically eroded the value of every transfer. With TMT, you receive institutional-grade security at consumer-friendly pricing.',
  },
  {
    icon: 'ri-government-line',
    title: 'Regulatory Partnership',
    text: 'We do not operate in isolation from the broader economic ecosystem. Rather, we position ourselves as a collaborative partner to regulatory authorities and governmental institutions across the Middle East, Africa, and Europe. By aligning our technological capabilities with national financial inclusion strategies and central bank digital currency initiatives, we actively contribute to the macroeconomic transformation toward cashless societies. We view compliance not as a burden, but as a shared mission to protect consumers and stabilize markets.',
  },
  {
    icon: 'ri-apps-line',
    title: 'Unified Ecosystem',
    text: 'Financial fragmentation is the enemy of prosperity. When users must navigate dozens of applications to manage their economic lives, efficiency suffers and opportunities are lost. Our mission culminates in the creation of a unified financial super-application — a single digital environment where individuals can accumulate savings, execute payments, and access growth-oriented financial instruments; where businesses can process payroll, manage vendor payments, and analyze cash flow; all within one cohesive, intelligent interface designed for the digital age.',
  },
];

const visionPillars = [
  {
    icon: 'ri-smartphone-line',
    title: 'Democratized Banking',
    text: 'We envision a future in which the distinction between those who have access to sophisticated financial services and those who do not has been permanently erased. In this world, every individual — regardless of geographic location, economic status, or educational background — carries within their mobile device the complete capabilities of a modern banking institution. This is not merely about convenience; it is about economic democratization. It is about ensuring that a farmer in a rural village has the same financial tools as an executive in a metropolitan capital. TMT exists to make this vision a reality.',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Unrivaled Trust',
    text: 'Trust is not granted; it is earned through consistent excellence, transparent operations, and uncompromising integrity. Our vision is to establish Trusted Money Transfer as the definitive financial partner of choice throughout the Middle East, Africa, and Europe — recognized not merely for the sophistication of our technology, but for the depth of our commitment to every stakeholder we serve. When people think of digital finance, we want them to think of TMT. When they think of trust, we want them to think of us.',
  },
  {
    icon: 'ri-global-line',
    title: 'Connected Ecosystem',
    text: 'We envision an interconnected digital financial ecosystem that transcends the traditional boundaries between consumers, merchants, financial institutions, and regulatory bodies. This ecosystem is not a closed platform but an open, collaborative environment where every participant contributes value and derives benefit. Merchants gain faster settlements and lower fees. Consumers enjoy seamless payments and personalized financial insights. Regulators receive real-time transparency into economic flows. Together, we build a financial network that strengthens everyone it touches.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Progressive Regulation',
    text: 'The financial regulations of tomorrow will be fundamentally different from those of today. They will be built upon principles of digital sovereignty, real-time transparency, and algorithmic compliance. We aspire to be not merely compliant with these emerging frameworks, but instrumental in their formulation and implementation. By working hand-in-hand with policymakers, we aim to shape the next generation of financial laws — laws that protect consumers without stifling innovation, that ensure stability while enabling growth, and that recognize the transformative potential of digital-first economies.',
  },
  {
    icon: 'ri-exchange-line',
    title: 'Cashless Transformation',
    text: 'Our ultimate vision extends beyond the mere digitization of financial transactions. We aspire to catalyze a comprehensive cultural and behavioral transformation — a permanent shift from cash-dependent lifestyles to digital-first financial consciousness. In the world we are building, children will grow up never needing to handle physical currency. Businesses will operate without cash registers. Governments will collect taxes with perfect transparency. This is not a distant dream; it is the inevitable future, and TMT is building the infrastructure to make it happen today.',
  },
];

export default function MissionVisionPage() {
  return (
    <div className="bg-background-50/95">
      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4">
            / Our Purpose
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight mb-6">
            Mission & <span className="text-primary-400">Vision</span>
          </h1>
          <p className="text-foreground-300 text-base md:text-lg leading-relaxed font-body max-w-2xl mx-auto mb-10">
            The principles that guide every decision, every innovation, and every interaction at
            Trusted Money Transfer. TMT: Your Future Digital Bank.
          </p>

          {/* Hero Banner Image */}
          <div className="relative overflow-hidden rounded-2xl border border-primary-700/20">
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/50 via-transparent to-transparent z-10" />
            <img
              src="https://readdy.ai/api/search-image?query=Inspiring%20abstract%20visualization%20of%20connected%20global%20financial%20network%20luminous%20golden%20nodes%20and%20pathways%20spanning%20across%20world%20map%20dark%20sophisticated%20background%20digital%20transformation%20concept%20premium%20fintech%20visualization&width=1400&height=500&seq=mv-hero-bnr-4&orientation=landscape"
              alt="TMT Global Financial Network — Mission and Vision"
              className="w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4">
              / Our Mission
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 mb-6">
              Empowering a Cashless World
            </h2>
            <p className="text-foreground-300 text-base leading-relaxed font-body">
              Our mission is the driving force behind every line of code we write, every partnership
              we forge, and every customer we serve. It is not a statement hung on a wall — it is a
              living, breathing commitment that shapes our daily operations and long-term strategy.
            </p>
          </div>

          {/* Mission Section Image */}
          <div className="relative overflow-hidden rounded-2xl mb-12 border border-primary-700/20">
            <div className="absolute inset-0 bg-gradient-to-r from-background-100/60 via-transparent to-transparent z-10" />
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20smartphone%20displaying%20digital%20banking%20app%20with%20golden%20coins%20and%20payment%20symbols%20floating%20upward%20dark%20luxurious%20background%20with%20soft%20bokeh%20lights%20financial%20empowerment%20concept%20premium%20product%20photography&width=1400&height=500&seq=mv-mission-img-5&orientation=landscape"
              alt="TMT Digital Banking — Financial Empowerment"
              className="w-full object-cover"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
            {missionPillars.map((pillar) => (
              <div
                key={pillar.title}
                className="p-6 md:p-8 bg-background-50/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-500"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-primary-600/30 rounded-full mb-5">
                  <i className={`${pillar.icon} text-primary-400 text-xl`} />
                </div>
                <h3 className="font-heading text-xl text-foreground-100 mb-3">{pillar.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{pillar.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4">
              / Our Vision
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 mb-6">
              A Bank in Every Pocket
            </h2>
            <p className="text-foreground-300 text-base leading-relaxed font-body">
              Our vision is not a destination but a direction — a North Star that guides our strategic
              investments, our product roadmap, and our organizational culture. It is ambitious by
              design, because transforming the future of money requires nothing less.
            </p>
          </div>

          {/* Vision Section Image */}
          <div className="relative overflow-hidden rounded-2xl mb-12 border border-primary-700/20">
            <div className="absolute inset-0 bg-gradient-to-l from-background-50/60 via-transparent to-transparent z-10" />
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20businesswoman%20in%20modern%20attire%20using%20digital%20wallet%20on%20smartphone%20while%20looking%20at%20futuristic%20city%20skyline%20through%20glass%20window%20golden%20hour%20lighting%20empowered%20finance%20concept%20editorial%20style&width=1400&height=500&seq=mv-vision-img-6&orientation=landscape"
              alt="TMT Vision — A Bank in Every Pocket"
              className="w-full object-cover"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
            {visionPillars.map((pillar) => (
              <div
                key={pillar.title}
                className="p-6 md:p-8 bg-background-100/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-500"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-primary-600/30 rounded-full mb-5">
                  <i className={`${pillar.icon} text-primary-400 text-xl`} />
                </div>
                <h3 className="font-heading text-xl text-foreground-100 mb-3">{pillar.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{pillar.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing statement */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-3xl mx-auto text-center">
          <Logo size="sm" linkToHome={false} showSlogan={false} className="mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-400 mb-4">
            TMT: Your Future Digital Bank
          </h2>
          <p className="text-foreground-300 text-base leading-relaxed mb-8 font-body">
            Our mission grounds us. Our vision propels us. Together, they form the foundation of
            everything we do at Trusted Money Transfer. We invite you to join us on this extraordinary
            journey toward a more inclusive, secure, and digital financial future.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="px-8 py-3.5 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              Open Account
            </Link>
            <Link
              to="/about"
              className="px-8 py-3.5 border border-primary-600/50 text-primary-400 font-label text-sm tracking-wider uppercase rounded-full hover:bg-primary-500/10 transition-colors whitespace-nowrap"
            >
              About TMT
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}